import { StorageConfig } from '@headless-workspace/config';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
} from '@headless-workspace/data';
import { z } from 'zod';
import { Repository, Result, ResultEmpty, ResultFailure, ResultSuccess } from '../types';
import { readStorageFile } from './StorageHelpers';
import { ContentAssetDTO, ContentAssetValue, mapContentAsset } from './contentAsset';
import {
    CountrySelector,
    FeatureFlagDTO,
    FeatureFlagValue,
    HeaderSlotsDTO,
    HeaderSlotsValue,
    mapCountrySelector,
    mapFeatureFlag,
    mapHeaderSlots,
} from './countrySpecificities';
import { mapMetadataSEO, MetaDataSEOByKeyDTO, MetadataSEOValue } from './seo';

type GetStorageFileParams<T extends z.ZodTypeAny, K> = {
    prefix: string;
    directory: string;
    fileName: string;
    type: T;
    mapper: (value: z.infer<T>) => K;
};

export interface StorageRepositorySpec {
    getMetadataSEO(prefix: string, fileName: string): Promise<Result<MetadataSEOValue>>;

    getHeaderSlots(prefix: string): Promise<Result<HeaderSlotsValue>>;

    getFeatureFlags(prefix: string): Promise<Result<FeatureFlagValue>>;

    getCountrySelector(prefix: string): Promise<Result<CountrySelector>>;

    getContentAssetTransformers(prefix: string): Promise<Result<ContentAssetValue>>;
}

export class StorageRepository extends Repository implements StorageRepositorySpec {
    path = '';

    constructor() {
        super(memoryCacheAuthStrategy, memoryCacheRefreshTokenStrategy, memoryCacheQueryParamStrategy);
    }

    private async getStorageFile<T extends z.ZodTypeAny, K>({
        prefix,
        directory,
        fileName,
        type,
        mapper,
    }: GetStorageFileParams<T, K>): Promise<Result<K>> {
        try {
            const data = await readStorageFile({
                directory,
                fileName: `${prefix}.${fileName}`,
            });

            if (!data) {
                return ResultEmpty();
            }

            const value = type.parse(data);

            return ResultSuccess(mapper(value));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailure(apiError.code, apiError.message);
        }
    }

    async getContentAssetTransformers(prefix: string): Promise<Result<ContentAssetValue>> {
        const result = await this.getStorageFile({
            prefix,
            directory: StorageConfig.directory.contentAsset,
            fileName: StorageConfig.filename.contentAsset,
            type: ContentAssetDTO,
            mapper: mapContentAsset,
        });
        if (result.type !== 'success') {
            return this.getStorageFile({
                prefix: 'default',
                directory: StorageConfig.directory.contentAsset,
                fileName: StorageConfig.filename.contentAsset,
                type: ContentAssetDTO,
                mapper: mapContentAsset,
            });
        }
        return result;
    }

    async getHeaderSlots(prefix: string): Promise<Result<HeaderSlotsValue>> {
        return await this.getStorageFile({
            prefix,
            directory: StorageConfig.directory.countrySpecificities,
            fileName: StorageConfig.filename.headerSlots,
            type: HeaderSlotsDTO,
            mapper: mapHeaderSlots,
        });
    }

    async getFeatureFlags(prefix: string): Promise<Result<FeatureFlagValue>> {
        return await this.getStorageFile({
            prefix,
            directory: StorageConfig.directory.countrySpecificities,
            fileName: StorageConfig.filename.featuresFlags,
            type: FeatureFlagDTO,
            mapper: mapFeatureFlag,
        });
    }

    async getCountrySelector(prefix: string): Promise<Result<CountrySelector>> {
        return await this.getStorageFile({
            prefix,
            directory: StorageConfig.directory.countrySpecificities,
            fileName: StorageConfig.filename.featuresFlags,
            type: FeatureFlagDTO,
            mapper: mapCountrySelector,
        });
    }

    async getMetadataSEO(prefix: string, fileName: string): Promise<Result<MetadataSEOValue>> {
        return await this.getStorageFile({
            prefix,
            directory: StorageConfig.directory.seo,
            fileName,
            type: MetaDataSEOByKeyDTO,
            mapper: mapMetadataSEO,
        });
    }
}
